import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db, auth } from './firebase';
import { collection, query, onSnapshot, where, doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { BUSINESS_TYPES, getBusinessType } from './businessTypes';
import { isAdminUser } from './adminConfig';
import { printSalesInvoice } from './printInvoice';
import { codFeeFor, formatFee } from './dueUtil';

const VALID_TABS = ['sales', 'purchases', 'returns', 'profit', 'receive', 'delivery'];

// আজকের তারিখ YYYY-MM-DD ফরম্যাটে বের করার হেল্পার
const getTodayString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ---- pure helpers moved outside component to prevent re-creation & ESLint warnings ----
const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

// ইনভয়েস আইডি স্ক্রিন থেকে কপি করলে সামনে "#" চলে আসে (যেমন "#INV-XXXX"), কিন্তু আসল
// invoiceId ফিল্ডে "#" থাকে না — তাই সার্চ করার আগে সেটা বাদ দেওয়া হচ্ছে।
const stripHash = (s) => s.trim().toLowerCase().replace(/^#/, '');

// তারিখ ফরম্যাট ফাংশন
const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  let d = dateStr?.toDate ? dateStr.toDate() : new Date(dateStr);
  if (isNaN(d.getTime())) {
    const cleaned = String(dateStr).replace('T', ' ').replace('Z', '');
    d = new Date(cleaned);
  }
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${day}-${months[d.getMonth()]}-${d.getFullYear()}, ${d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
};

const getItemDate = (item) => {
  const rawDate = item.createdAt || item.date || item.dateTime;
  if (!rawDate) return null;
  let d = rawDate?.toDate ? rawDate.toDate() : new Date(rawDate);
  return isNaN(d.getTime()) ? null : d;
};

const sortByDateDesc = (list) => {
  return [...list].sort((a, b) => {
    const da = getItemDate(a);
    const dbb = getItemDate(b);
    if (!da && !dbb) return 0;
    if (!da) return 1;
    if (!dbb) return -1;
    return dbb - da;
  });
};

// ---- Purchase কালেকশনের ফিল্ড নাম প্রজেক্ট ভেদে ভিন্ন হতে পারে ----
// তাই একাধিক সম্ভাব্য ফিল্ড নাম ফলব্যাক হিসেবে চেক করা হচ্ছে।
// যদি আপনার আসল Purchase.jsx-এ অন্য ফিল্ড নাম ব্যবহৃত হয়, আমাকে সেই
// কোডটা দিলে এখানে ঠিক করে দেব।
const getSupplierName = (p) => p.supplierName || p.supplier || p.name || 'N/A';
const getPurchaseInvoice = (p) => p.purchaseId || p.invoiceId || p.id || 'N/A';
// Purchase.js সেভ করে totalCost ফিল্ডে — আগে এখানে totalBill/total/grandTotal খোঁজা হতো,
// যেগুলো কখনোই সেট হতো না, তাই Purchase Report-এ সব সময় ৳0 দেখাত।
const getPurchaseTotal = (p) => Number(p.totalCost ?? p.totalBill ?? p.total ?? p.grandTotal ?? 0);
// সাপ্লায়ারকে আংশিক টাকা দেওয়ার কোনো ফিচার এখনো নেই — Purchase ফর্মে কেনার সময়ই পুরো
// দাম ধরে নেওয়া হয়, তাই Paid = Total, Due = 0। ভবিষ্যতে supplier due ফিচার এলে এটা বদলাতে হবে।
const getPurchasePaid = (p) => (p.paid !== undefined ? Number(p.paid) : getPurchaseTotal(p));
const getPurchaseDue = (p) => {
  if (p.dueAmount !== undefined) return Number(p.dueAmount);
  return Math.max(0, getPurchaseTotal(p) - getPurchasePaid(p));
};

// Purchase.js-এর নিজের প্রিন্ট বাটন কাজ করে শুধু ফর্ম জমা দেওয়ার ঠিক পরের একটা ephemeral
// preview অবজেক্টে (আলাদা ফিল্ড নাম -- finalUnitCost, totalCost সরাসরি)। এখানে Reports-এর
// এই মডালটা Firestore-এ সেভ হওয়া আসল ডকুমেন্ট দেখায়, যেটার শেপ একটু আলাদা -- তাই এই
// প্রিন্ট ফাংশনটা উপরের getPurchaseInvoice/getSupplierName/getPurchaseTotal হেল্পারগুলোই
// ব্যবহার করে, যাতে মডালে যা দেখা যাচ্ছে প্রিন্টেও ঠিক তাই আসে।
const printPurchaseVoucher = (p) => {
  if (!p) return;
  const printWindow = window.open('', '_blank', 'width=600,height=600');
  if (!printWindow) {
    alert('Print window was blocked by the browser. Please allow popups for this site and try again.');
    return;
  }
  const invoiceId = getPurchaseInvoice(p);
  const supplierLine = `${getSupplierName(p)}${p.supplierPhone && p.supplierPhone !== 'N/A' ? ` (${p.supplierPhone})` : ''}`;
  printWindow.document.write(`
    <html>
      <head>
        <title>${invoiceId}</title>
        <style>
          body { font-family: 'Poppins', sans-serif; padding: 20px; color: #333; }
          .invoice-box { max-width: 400px; margin: auto; padding: 20px; border: 1px solid #eee; box-shadow: 0 0 10px rgba(0,0,0,0.1); font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { padding: 5px; text-align: left; border-bottom: 1px solid #eee; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
        </style>
      </head>
      <body>
        <div class="invoice-box">
          <div style="text-align:center; margin-bottom:15px;">
            <h2 style="margin:0; color:#4a00e0; font-size:18px;">PB KIZUNA COMPANY LIMITED</h2>
            <p style="margin:4px 0; font-size:10px;">H-10, R-2, Section-2, Block-D, Aftabnagar, Dhaka</p>
            <p style="margin:0; font-size:10px;">Mobile: 01884-711844</p>
            <hr style="border: dashed 1px #000; margin: 10px 0;"/>
            <h4 style="margin:0; font-size:12px;">PURCHASE VOUCHER / INVOICE</h4>
          </div>
          <p style="margin: 2px 0;"><b>Added By:</b> ${p.addedBy || 'N/A'}</p>
          <p style="margin: 2px 0;"><b>Business Type:</b> ${getBusinessType(p)}</p>
          <p style="margin: 2px 0;"><b>Date:</b> ${formatDateTime(p.createdAt || p.date)}</p>
          <p style="margin: 2px 0;"><b>Purchase ID:</b> #${invoiceId}</p>
          <p style="margin: 2px 0;"><b>Supplier:</b> ${supplierLine}</p>
          <hr style="border: dashed 1px #000; margin: 10px 0;"/>
          <table>
            <thead>
              <tr>
                <th>ITEM</th>
                <th class="text-center">QTY</th>
                <th class="text-right">UNIT COST</th>
                <th class="text-right">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>${p.productName || getSupplierName(p)}</b></td>
                <td class="text-center">${p.quantity}</td>
                <td class="text-right">${formatTk(p.costPrice)}</td>
                <td class="text-right">${formatTk(getPurchaseTotal(p))}</td>
              </tr>
            </tbody>
          </table>
          <div style="margin-top:15px; text-align:right; font-size:12px;">
            ${p.baseRate > 0 ? `<p style="margin:2px 0;">Base Rate (x${p.quantity}): ৳ ${formatTk(p.baseRate * p.quantity)}</p>` : ''}
            ${p.otherCost > 0 ? `<p style="margin:2px 0;">Transport / Other Cost: +৳ ${formatTk(p.otherCost)}</p>` : ''}
            <h3 style="margin:5px 0; font-size:14px;">Total Cost: ৳ ${formatTk(getPurchaseTotal(p))}</h3>
          </div>
          <p style="text-align:center; margin-top:20px; font-weight:bold; font-size:11px;">Thank you!</p>
        </div>
        <script>
          document.title = "${invoiceId}";
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

const Reports = () => {
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(requestedTab) ? requestedTab : 'sales');

  // ---- URL-এর ?tab= প্যারামিটার বদলালে (যেমন Home ড্যাশবোর্ডের KPI কার্ড থেকে) সেই ট্যাবে সুইচ করা হবে ----
  useEffect(() => {
    if (VALID_TABS.includes(requestedTab)) setActiveTab(requestedTab);
  }, [requestedTab]);

  // ---- মূল ডেটা (Firestore থেকে রিয়েল-টাইম) ----
  const [salesList, setSalesList] = useState([]);
  const [purchaseList, setPurchaseList] = useState([]);
  const [returnList, setReturnList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [receiveList, setReceiveList] = useState([]);

  // ---- ডেট-ফিল্টার করা ডেটা ----
  const [filteredSales, setFilteredSales] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [filteredReceives, setFilteredReceives] = useState([]);

  // ---- প্রতিটা ট্যাবের জন্য আলাদা সার্চ টার্ম ----
  const [salesSearch, setSalesSearch] = useState('');
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [returnSearch, setReturnSearch] = useState('');

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);

  const [startDate, setStartDate] = useState(getTodayString());
  const [endDate, setEndDate] = useState(getTodayString());
  const [activeDatePreset, setActiveDatePreset] = useState('Today');
  const [businessTypeFilter, setBusinessTypeFilter] = useState('All');
  const isAdmin = isAdminUser(auth.currentUser);

  const [masterExporting, setMasterExporting] = useState(false);

  // Firebase থেকে ডেটা লোড করা — ইউজার যে তারিখ-রেঞ্জ সিলেক্ট করেছে শুধু সেটুকুই সরাসরি
  // Firestore থেকে আনা হয় (আগে পুরো sales/purchases/returns history টেনে এনে পরে
  // ক্লায়েন্ট-সাইডে ফিল্টার করা হতো, যেটা ডেটা বাড়ার সাথে সাথে read খরচ বাড়িয়ে দিতো)।
  useEffect(() => {
    const rangeStart = new Date(startDate + 'T00:00:00');
    const rangeEnd = new Date(endDate + 'T23:59:59.999');

    const unsubSales = onSnapshot(query(collection(db, "sales"), where("createdAt", ">=", rangeStart), where("createdAt", "<=", rangeEnd)), (snap) => {
      setSalesList(sortByDateDesc(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    });
    const unsubPurchases = onSnapshot(query(collection(db, "purchases"), where("createdAt", ">=", rangeStart), where("createdAt", "<=", rangeEnd)), (snap) => {
      setPurchaseList(sortByDateDesc(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    });
    const unsubReturns = onSnapshot(query(collection(db, "returns"), where("createdAt", ">=", rangeStart), where("createdAt", "<=", rangeEnd)), (snap) => {
      setReturnList(sortByDateDesc(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    });
    const unsubProducts = onSnapshot(query(collection(db, "products")), (snap) => {
      setProductList(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubReceives = onSnapshot(query(collection(db, "duePayments"), where("createdAt", ">=", rangeStart), where("createdAt", "<=", rangeEnd)), (snap) => {
      setReceiveList(sortByDateDesc(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    });
    return () => { unsubSales(); unsubPurchases(); unsubReturns(); unsubProducts(); unsubReceives(); };
  }, [startDate, endDate]);

  // ডেট ফিল্টার
  useEffect(() => {
    const filterFn = (item) => {
      const d = getItemDate(item);
      if (!d) return false;
      const itemDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return itemDate >= startDate && itemDate <= endDate;
    };
    setFilteredSales(salesList.filter(filterFn));
    setFilteredPurchases(purchaseList.filter(filterFn));
    setFilteredReturns(returnList.filter(filterFn));
    setFilteredReceives(receiveList.filter(filterFn));
  }, [salesList, purchaseList, returnList, receiveList, startDate, endDate]);

  // ---- Business Type ফিল্টার (ডেট ফিল্টারের উপরে, সার্চের নিচে প্রয়োগ হয়) ----
  // "All" থাকলে পুরো ইনভয়েস/রিটার্নের আসল totalBill/totalRefund ব্যবহার হয়;
  // নির্দিষ্ট টাইপ সিলেক্ট করলে শুধু মিলে যাওয়া আইটেমগুলোর যোগফল দেখানো হয় —
  // ডিসকাউন্ট/ডেলিভারি চার্জ তখন প্রতিটা লাইনে ভাগ করে হিসাব করা হয় না (আনুমানিক সংখ্যা)।
  const getSaleMatchingItems = useCallback((sale) => {
    const items = sale.items || [];
    return businessTypeFilter === 'All' ? items : items.filter(it => getBusinessType(it) === businessTypeFilter);
  }, [businessTypeFilter]);
  const getSaleAmountForType = useCallback((sale) => {
    if (businessTypeFilter === 'All') return Number(sale.totalBill || 0);
    return getSaleMatchingItems(sale).reduce((s, it) => s + (Number(it.finalItemTotal ?? (it.price * it.qty)) || 0), 0);
  }, [businessTypeFilter, getSaleMatchingItems]);
  const getReturnAmountForType = useCallback((ret) => {
    if (businessTypeFilter === 'All') return Number(ret.totalRefund || 0);
    return (ret.items || []).filter(it => getBusinessType(it) === businessTypeFilter).reduce((s, it) => s + Number(it.returnTotal || 0), 0);
  }, [businessTypeFilter]);

  const typeFilteredSales = useMemo(() => {
    if (businessTypeFilter === 'All') return filteredSales;
    return filteredSales.filter(s => getSaleMatchingItems(s).length > 0);
  }, [filteredSales, businessTypeFilter, getSaleMatchingItems]);

  const typeFilteredPurchases = useMemo(() => {
    if (businessTypeFilter === 'All') return filteredPurchases;
    return filteredPurchases.filter(p => getBusinessType(p) === businessTypeFilter);
  }, [filteredPurchases, businessTypeFilter]);

  const typeFilteredReturns = useMemo(() => {
    if (businessTypeFilter === 'All') return filteredReturns;
    return filteredReturns.filter(r => (r.items || []).some(it => getBusinessType(it) === businessTypeFilter));
  }, [filteredReturns, businessTypeFilter]);

  // ---- সার্চ প্রয়োগ করা (Business Type ফিল্টারের উপরে) ----
  const searchedSales = useMemo(() => {
    const term = stripHash(salesSearch);
    if (!term) return typeFilteredSales;
    return typeFilteredSales.filter(s =>
      (s.invoiceId || '').toLowerCase().includes(term) ||
      (s.name || '').toLowerCase().includes(term) ||
      (s.phone || '').toLowerCase().includes(term) ||
      (s.phone2 || '').toLowerCase().includes(term)
    );
  }, [typeFilteredSales, salesSearch]);

  const searchedPurchases = useMemo(() => {
    const term = stripHash(purchaseSearch);
    if (!term) return typeFilteredPurchases;
    return typeFilteredPurchases.filter(p =>
      String(getPurchaseInvoice(p)).toLowerCase().includes(term) ||
      getSupplierName(p).toLowerCase().includes(term)
    );
  }, [typeFilteredPurchases, purchaseSearch]);

  const searchedReturns = useMemo(() => {
    const term = stripHash(returnSearch);
    if (!term) return typeFilteredReturns;
    return typeFilteredReturns.filter(r =>
      (r.returnId || '').toLowerCase().includes(term) ||
      (r.originalInvId || '').toLowerCase().includes(term) ||
      (r.customerName || '').toLowerCase().includes(term) ||
      (r.reason || '').toLowerCase().includes(term)
    );
  }, [typeFilteredReturns, returnSearch]);

  // ---- Receivables (বাকি টাকা) ক্যালকুলেশন ----
  const receivablesData = useMemo(() => {
    return typeFilteredSales.filter(s => Number(s.dueAmount || 0) > 0);
  }, [typeFilteredSales]);

  const totalReceivables = useMemo(() => {
    return receivablesData.reduce((sum, s) => sum + Number(s.dueAmount || 0), 0);
  }, [receivablesData]);

  // ---- প্রতিটা ট্যাবের টোটাল (সার্চ করা লিস্টের উপর ভিত্তি করে) ----
  const salesTotals = useMemo(() => ({
    bill: searchedSales.reduce((s, r) => s + getSaleAmountForType(r), 0),
    paid: searchedSales.reduce((s, r) => s + Number(r.paid || 0), 0),
    due: searchedSales.reduce((s, r) => s + Number(r.dueAmount || 0), 0),
  }), [searchedSales, getSaleAmountForType]);

  const purchaseTotals = useMemo(() => ({
    bill: searchedPurchases.reduce((s, r) => s + getPurchaseTotal(r), 0),
    paid: searchedPurchases.reduce((s, r) => s + getPurchasePaid(r), 0),
    due: searchedPurchases.reduce((s, r) => s + getPurchaseDue(r), 0),
  }), [searchedPurchases]);

  const returnTotals = useMemo(() => ({
    refund: searchedReturns.reduce((s, r) => s + getReturnAmountForType(r), 0),
  }), [searchedReturns, getReturnAmountForType]);

  const receiveTotals = useMemo(() => ({
    amount: filteredReceives.reduce((s, r) => s + Number(r.amount || 0), 0),
  }), [filteredReceives]);

  // ---- সহজ প্রফিট হিসাব (ক্যাশ-ফ্লো ভিত্তিক: বিক্রি − ক্রয় − রিটার্ন রিফান্ড) ----
  // নোট: এটা প্রোডাক্ট-ভিত্তিক cost-price দিয়ে সঠিক gross-margin হিসাব না,
  // কারণ প্রতিটা সেলের সময় কেনা-দাম (cost price) আলাদা করে সেভ হয় না।
  // এটা সিলেক্টেড সময়ের মোট নগদ প্রবাহ ভিত্তিক আনুমানিক প্রফিট।
  const profitSummary = useMemo(() => {
    const billedAmount = typeFilteredSales.reduce((s, r) => s + getSaleAmountForType(r), 0);
    // ---- ডেলিভারি চার্জ কখনোই প্রফিট না, কিন্তু কাস্টমার-পেইড আর শপ-পেইড দুটোর হিসাব আলাদা:
    // কাস্টমার দিলে সেটা বিলের (totalBill) ভেতরেই ঢুকে থাকে (শুধু "All" মোডে, কারণ item-level
    // হিসাবে ডেলিভারি এমনিতেই নেই) — তাই সেটা রেভিনিউ থেকে বাদ দিতে হয় (আসল বিক্রি না)।
    // শপ দিলে সেটা বিলেই ঢোকে না (কাস্টমারকে চার্জ করা হয়নি), কিন্তু দোকানের আসল নগদ খরচ —
    // তাই সেটা রেভিনিউ থেকে না, সরাসরি প্রফিট থেকে বাদ যায়। আগে দুটোকেই রেভিনিউ থেকে বাদ
    // দেওয়া হতো, যার ফলে শপ-পেইড ডেলিভারির জন্য রেভিনিউ ভুলভাবে কমে যেত (ডাবল-কাউন্ট)।
    const shopPaidDelivery = typeFilteredSales.reduce((s, r) => s + (r.deliveryPaidBy === 'shop' ? Number(r.deliveryCharge || 0) : 0), 0);
    const customerPaidDelivery = businessTypeFilter === 'All'
      ? typeFilteredSales.reduce((s, r) => s + (r.deliveryPaidBy !== 'shop' ? Number(r.deliveryCharge || 0) : 0), 0)
      : 0;
    const totalSalesRevenue = billedAmount - customerPaidDelivery;
    const totalPurchaseCost = typeFilteredPurchases.reduce((s, r) => s + getPurchaseTotal(r), 0);
    const totalReturnRefund = typeFilteredReturns.reduce((s, r) => s + getReturnAmountForType(r), 0);
    // Pathao COD টাকার ১% আলাদা কেটে নেয় -- ডেলিভারি চার্জের বাইরে, আর কে ডেলিভারি দিচ্ছে তার সাথে সম্পর্কহীন,
    // সবসময় দোকানের খরচ। রেভিনিউ থেকে না, সরাসরি প্রফিট থেকে বাদ (শপ-পেইড ডেলিভারির মতোই)। বিস্তারিত dueUtil.js-এ।
    const codFee = typeFilteredSales.reduce((s, r) => s + codFeeFor(r), 0);
    const netProfit = totalSalesRevenue - totalPurchaseCost - totalReturnRefund - shopPaidDelivery - codFee;
    return { totalSalesRevenue, deliveryCharge: customerPaidDelivery + shopPaidDelivery, customerPaidDelivery, shopPaidDelivery, codFee, totalPurchaseCost, totalReturnRefund, netProfit };
  }, [typeFilteredSales, typeFilteredPurchases, typeFilteredReturns, businessTypeFilter, getSaleAmountForType, getReturnAmountForType]);

  // ---- ডেলিভারি চার্জ রিপোর্ট: ইনভয়েস অনুযায়ী, সিলেক্ট করা তারিখ-রেঞ্জের ----
  const deliveryData = useMemo(() => {
    return typeFilteredSales.filter(s => Number(s.deliveryCharge || 0) > 0);
  }, [typeFilteredSales]);

  // কাস্টমার দিলে টাকাটা বিলের ভেতরে আসে (দোকানের আয় না, কুরিয়ারের);
  // দোকান দিলে বিলে যোগ হয় না, ওটা সরাসরি দোকানের খরচ। দুই ক্ষেত্রেই প্রফিট থেকে বাদ যায়।
  const deliveryTotals = useMemo(() => ({
    all: deliveryData.reduce((s, r) => s + Number(r.deliveryCharge || 0), 0),
    customer: deliveryData.filter(r => r.deliveryPaidBy !== 'shop').reduce((s, r) => s + Number(r.deliveryCharge || 0), 0),
    shop: deliveryData.filter(r => r.deliveryPaidBy === 'shop').reduce((s, r) => s + Number(r.deliveryCharge || 0), 0),
  }), [deliveryData]);

  // ---- এক ট্যাবের এক্সেল এক্সপোর্ট ----
  // ---- সেল হয়ে যাওয়ার পর ডেলিভারি চার্জ কে দিয়েছে সেটা বদলানো (ভুলে ভুল সিলেক্ট হলে) ----
  // কাস্টমার → দোকান হলে চার্জটা বিল থেকে বাদ যায়, উল্টোটা হলে বিলে যোগ হয়;
  // সেই অনুযায়ী বাকি টাকা (due) আর ফেরতযোগ্য টাকাও (return) নতুন করে হিসাব হয়।
  const changeDeliveryPayer = async (sale) => {
    // ---- ফ্রন্টএন্ডে বাটন লুকানো ছাড়াও এখানে সরাসরি চেক করা হচ্ছে, যাতে কেউ ডেভ-টুলস দিয়ে
    // সরাসরি এই ফাংশন কল করলেও Admin ছাড়া আটকে যায় ----
    if (!isAdmin) return alert("Only the Admin account can change the delivery charge payer.");

    const charge = Number(sale.deliveryCharge || 0);
    const wasShop = sale.deliveryPaidBy === 'shop';
    const paid = Number(sale.paid || 0);
    const oldTotal = Number(sale.totalBill || 0);
    const newTotal = Math.max(0, oldTotal + (wasShop ? charge : -charge));
    const newDue = Math.max(0, newTotal - paid);
    const newReturn = Math.max(0, paid - newTotal);
    const newPayerName = wasShop
      ? (sale.name || 'Customer')
      : (auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Kizuna');

    const confirmText =
      `Change who paid the delivery charge?\n\n` +
      `Invoice: #${sale.invoiceId}\n` +
      `Delivery charge: ৳ ${formatTk(charge)}\n` +
      `${wasShop ? 'Shop' : 'Customer'}  →  ${wasShop ? 'Customer' : 'Shop'} (${newPayerName})\n\n` +
      `Total bill: ৳ ${formatTk(oldTotal)}  →  ৳ ${formatTk(newTotal)}\n` +
      `Due: ৳ ${formatTk(sale.dueAmount || 0)}  →  ৳ ${formatTk(newDue)}` +
      (newReturn > 0 ? `\nRefund owed to customer: ৳ ${formatTk(newReturn)}` : '');

    if (!window.confirm(confirmText)) return;

    try {
      await updateDoc(doc(db, "sales", sale.id), {
        deliveryPaidBy: wasShop ? 'customer' : 'shop',
        deliveryPaidByName: newPayerName,
        totalBill: newTotal,
        dueAmount: newDue,
        returnAmount: newReturn,
        // ---- ট্র্যাকিং-এর জন্য প্রতিটা পরিবর্তনের ইতিহাস ইনভয়েসের সাথেই সেভ থাকছে ----
        deliveryPayerChangeLog: arrayUnion({
          from: wasShop ? 'shop' : 'customer',
          to: wasShop ? 'customer' : 'shop',
          oldTotal, newTotal,
          changedBy: auth.currentUser?.displayName || auth.currentUser?.email || 'Admin',
          changedAt: new Date().toISOString()
        })
      });
    } catch (err) {
      alert("Could not update the invoice: " + err.message);
    }
  };

  // ---- দ্রুত তারিখ বাছাই (Today / Yesterday / This Month); কাস্টম রেঞ্জ দুইটা ডেট বক্স দিয়ে ----
  const applyDatePreset = (preset, label) => {
    const toStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (preset === 'month') {
      const now = new Date();
      setStartDate(toStr(new Date(now.getFullYear(), now.getMonth(), 1)));
      setEndDate(toStr(now));
      setActiveDatePreset(label);
      return;
    }
    const d = new Date();
    d.setDate(d.getDate() - preset);
    setStartDate(toStr(d));
    setEndDate(toStr(d));
    setActiveDatePreset(label);
  };

  // ---- Due Receive এন্ট্রিতে পুরো sale ডকুমেন্ট থাকে না (শুধু invoiceId/amount), তাই
  // ইনভয়েস আইডিতে ক্লিক করলে সরাসরি sales কালেকশন থেকে আসল সেলটা টেনে দেখানো হচ্ছে ----
  const viewReceiveInvoice = async (r) => {
    if (!r.saleId) return alert("Original sale not found for this payment.");
    try {
      const snap = await getDoc(doc(db, "sales", r.saleId));
      if (!snap.exists()) return alert("The original sale invoice could not be found (it may have been deleted).");
      setSelectedInvoice({ id: snap.id, ...snap.data() });
    } catch (err) {
      alert("Could not load the invoice: " + err.message);
    }
  };

  const getSaleTypesLabel = (sale) => Array.from(new Set((sale.items || []).map(it => getBusinessType(it)))).join(', ') || 'Saree';
  const getReturnTypesLabel = (ret) => Array.from(new Set((ret.items || []).map(it => getBusinessType(it)))).join(', ') || 'Saree';

  const exportExcel = () => {
    let dataToExport = [];
    let fileName = "Report.xlsx";
    if (activeTab === 'sales') {
      dataToExport = searchedSales.map(s => ({
        Date: formatDateTime(s.createdAt),
        Invoice: s.invoiceId,
        BusinessType: businessTypeFilter === 'All' ? getSaleTypesLabel(s) : businessTypeFilter,
        Customer: s.name || 'Walk-In',
        Phone: [s.phone, s.phone2].filter(Boolean).join(', ') || '',
        Total: getSaleAmountForType(s),
        Paid: s.paid,
        Due: s.dueAmount || 0
      }));
      fileName = "Sales_Report.xlsx";
    } else if (activeTab === 'purchases') {
      dataToExport = searchedPurchases.map(p => ({
        Date: formatDateTime(p.createdAt),
        Invoice: getPurchaseInvoice(p),
        BusinessType: getBusinessType(p),
        Supplier: getSupplierName(p),
        Total: getPurchaseTotal(p),
        Paid: getPurchasePaid(p),
        Due: getPurchaseDue(p)
      }));
      fileName = "Purchase_Report.xlsx";
    } else if (activeTab === 'returns') {
      dataToExport = searchedReturns.map(r => ({
        Date: formatDateTime(r.createdAt),
        ReturnID: r.returnId,
        BusinessType: businessTypeFilter === 'All' ? getReturnTypesLabel(r) : businessTypeFilter,
        OriginalInvoice: r.originalInvId,
        Customer: r.customerName,
        Reason: r.reason,
        Refund: getReturnAmountForType(r)
      }));
      fileName = "Returns_Report.xlsx";
    } else if (activeTab === 'receive') {
      dataToExport = filteredReceives.map(r => ({
        Date: formatDateTime(r.createdAt),
        Invoice: r.invoiceId,
        Customer: r.customerName,
        Phone: r.customerPhone,
        Amount: r.amount
      }));
      fileName = "Due_Receive_Report.xlsx";
    } else if (activeTab === 'delivery') {
      dataToExport = deliveryData.map(s => ({
        Date: formatDateTime(s.createdAt),
        Invoice: s.invoiceId,
        Customer: s.name || 'Walk-In',
        Phone: [s.phone, s.phone2].filter(Boolean).join(', ') || '',
        PaidBy: s.deliveryPaidBy === 'shop' ? `Shop (${s.deliveryPaidByName || 'Kizuna'})` : 'Customer',
        DeliveryCharge: Number(s.deliveryCharge || 0)
      }));
      fileName = "Delivery_Charge_Report.xlsx";
    } else if (activeTab === 'profit') {
      dataToExport = [
        { Item: 'Total Sales Revenue (excl. delivery)', Amount: profitSummary.totalSalesRevenue },
        { Item: 'Delivery Charge (not profit)', Amount: profitSummary.deliveryCharge },
        { Item: 'Pathao COD Fee (1%)', Amount: -profitSummary.codFee },
        { Item: 'Total Purchase Cost', Amount: -profitSummary.totalPurchaseCost },
        { Item: 'Total Return Refund', Amount: -profitSummary.totalReturnRefund },
        { Item: 'Net Profit (Estimated)', Amount: profitSummary.netProfit },
      ];
      fileName = "Profit_Report.xlsx";
    }
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, fileName);
  };

  // ---- MASTER REPORT: একসাথে সব ডেটা একটা ফাইলে, প্রতিটা আলাদা শীটে ----
  const exportMasterReport = () => {
    setMasterExporting(true);
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Sales
      const salesSheet = typeFilteredSales.map(s => ({
        Date: formatDateTime(s.createdAt),
        Invoice: s.invoiceId,
        BusinessType: businessTypeFilter === 'All' ? getSaleTypesLabel(s) : businessTypeFilter,
        Customer: s.name || 'Walk-In',
        Phone: [s.phone, s.phone2].filter(Boolean).join(', ') || '',
        Total: getSaleAmountForType(s),
        Paid: s.paid,
        Due: s.dueAmount || 0
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(salesSheet), "Sales");

      // Sheet 2: Purchases
      const purchaseSheet = typeFilteredPurchases.map(p => ({
        Date: formatDateTime(p.createdAt),
        Invoice: getPurchaseInvoice(p),
        BusinessType: getBusinessType(p),
        Supplier: getSupplierName(p),
        Total: getPurchaseTotal(p),
        Paid: getPurchasePaid(p),
        Due: getPurchaseDue(p)
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(purchaseSheet), "Purchases");

      // Sheet 3: Returns
      const returnSheet = typeFilteredReturns.map(r => ({
        Date: formatDateTime(r.createdAt),
        ReturnID: r.returnId,
        BusinessType: businessTypeFilter === 'All' ? getReturnTypesLabel(r) : businessTypeFilter,
        OriginalInvoice: r.originalInvId,
        Customer: r.customerName,
        Reason: r.reason,
        Refund: getReturnAmountForType(r)
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(returnSheet), "Returns");

      // Sheet 3b: Due Receives
      const receiveSheet = filteredReceives.map(r => ({
        Date: formatDateTime(r.createdAt),
        Invoice: r.invoiceId,
        Customer: r.customerName,
        Phone: r.customerPhone,
        Amount: r.amount
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(receiveSheet), "Due Receives");

      // Sheet 3c: Delivery Charges (ইনভয়েস অনুযায়ী)
      const deliverySheet = deliveryData.map(s => ({
        Date: formatDateTime(s.createdAt),
        Invoice: s.invoiceId,
        Customer: s.name || 'Walk-In',
        Phone: [s.phone, s.phone2].filter(Boolean).join(', ') || '',
        PaidBy: s.deliveryPaidBy === 'shop' ? `Shop (${s.deliveryPaidByName || 'Kizuna'})` : 'Customer',
        DeliveryCharge: Number(s.deliveryCharge || 0)
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(deliverySheet), "Delivery Charges");

      // Sheet 4: Profit Summary
      const profitSheet = [
        { Item: 'Total Sales Revenue (excl. delivery)', Amount: profitSummary.totalSalesRevenue },
        { Item: 'Delivery Charge (not profit)', Amount: profitSummary.deliveryCharge },
        { Item: 'Pathao COD Fee (1%)', Amount: -profitSummary.codFee },
        { Item: 'Total Purchase Cost', Amount: -profitSummary.totalPurchaseCost },
        { Item: 'Total Return Refund', Amount: -profitSummary.totalReturnRefund },
        { Item: 'Net Profit (Estimated)', Amount: profitSummary.netProfit },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(profitSheet), "Profit Summary");

      // Sheet 5: Product Setup / Stock (products কালেকশন থেকে)
      const stockSheet = productList
        .filter(p => businessTypeFilter === 'All' || getBusinessType(p) === businessTypeFilter)
        .map(p => ({
          Name: p.name,
          BusinessType: getBusinessType(p),
          Category: p.category || 'General',
          Price: p.price,
          Stock: p.stock
        }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(stockSheet), "Product Setup");

      // ---- Accounts & Ledger এবং Recycle Bin এখনও যোগ করা হয়নি ----
      // কারণ ওই দুটো পেজের কোড/কালেকশন-স্ট্রাকচার এখনো দেখা হয়নি।
      // Accounts.jsx এবং Recycle Bin এর কোড দিলে এই দুটো শীটও
      // এখানে ঠিকভাবে যোগ করে দেওয়া হবে।

      const fileName = `Master_Report_${startDate}_to_${endDate}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } finally {
      setMasterExporting(false);
    }
  };

  return (
    <div style={{ padding: '20px', background: '#f4f7fe', minHeight: '100vh' }}>
      
      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {VALID_TABS.map(tab => (
          <button 
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{ 
              padding: '12px 24px', borderRadius: '12px', border: 'none', 
              background: activeTab === tab ? '#4a00e0' : '#fff', 
              color: activeTab === tab ? '#fff' : '#333', 
              fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' 
            }}
          >
            {tab === 'sales' && '📊 Sales Report'}
            {tab === 'purchases' && '📦 Purchase Report'}
            {tab === 'returns' && '🔄 Product Return'}
            {tab === 'profit' && '📈 Profit Report'}
            {tab === 'receive' && '💰 Due Receive'}
            {tab === 'delivery' && '🚚 Delivery Charge'}
          </button>
        ))}

        {/* ---- MASTER REPORT বাটন: সব রিপোর্ট একসাথে এক ফাইলে ---- */}
        <button
          onClick={exportMasterReport}
          disabled={masterExporting}
          style={{
            marginLeft: 'auto',
            padding: '12px 24px',
            borderRadius: '12px',
            border: 'none',
            background: '#4a00e0',
            backgroundImage: 'linear-gradient(135deg, #4a00e0, #8e2de2)',
            color: '#fff',
            fontWeight: 'bold',
            cursor: masterExporting ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 10px rgba(74,0,224,0.3)',
            opacity: masterExporting ? 0.7 : 1
          }}
        >
          {masterExporting ? 'Preparing...' : '⬇️ Master Report (All in One Excel)'}
        </button>
      </div>

      <div className="white-box" style={{ padding: '30px', borderRadius: '25px', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        
        {/* Header & Filters */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>Master Reports – {activeTab.toUpperCase()}</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {[
              { label: 'Today', days: 0 },
              { label: 'Yesterday', days: 1 },
              { label: 'This Month', days: 'month' }
            ].map(preset => (
              <button
                key={preset.label}
                onClick={() => applyDatePreset(preset.days, preset.label)}
                style={{
                  padding: '8px 14px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '12px',
                  border: `1px solid ${activeDatePreset === preset.label ? '#4a00e0' : '#eee'}`,
                  background: activeDatePreset === preset.label ? '#4a00e0' : '#f8f9ff',
                  color: activeDatePreset === preset.label ? '#fff' : '#4a00e0'
                }}
              >
                {preset.label}
              </button>
            ))}
            <select className="input-field" value={businessTypeFilter} onChange={e => setBusinessTypeFilter(e.target.value)} style={{ width: '170px', marginBottom: 0 }}>
              <option value="All">All Business Types</option>
              {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input type="date" className="input-field" value={startDate} onChange={e => { setStartDate(e.target.value); setActiveDatePreset(null); }} style={{ width: '150px', marginBottom: 0 }} />
            <input type="date" className="input-field" value={endDate} onChange={e => { setEndDate(e.target.value); setActiveDatePreset(null); }} style={{ width: '150px', marginBottom: 0 }} />
            {activeTab !== 'profit' && (
              <button onClick={exportExcel} style={{ background: '#27ae60', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>EXCEL</button>
            )}
          </div>
        </div>

        {/* --- SALES TAB CONTENT --- */}
        {activeTab === 'sales' && (
          <>
            {/* Receivables Summary Card */}
            <div className="hover-lift" style={{ 
              background: '#fff', padding: '20px', borderRadius: '20px', 
              borderLeft: '6px solid #f59e0b', boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
              marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '15px', width: 'fit-content', minWidth: '300px'
            }}>
              <div style={{ fontSize: '30px' }}>👤</div>
              <div>
                <p style={{ margin: 0, color: '#666', fontSize: '14px', fontWeight: 'bold' }}>TOTAL RECEIVABLES (Due Amount)</p>
                <h2 style={{ margin: 0, color: '#f59e0b', fontSize: '24px' }}>৳ {formatTk(totalReceivables)}</h2>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
              <h4 style={{ margin: 0, color: '#4a00e0' }}>All Sales Transactions</h4>
              {/* ---- সার্চ বক্স ---- */}
              <input
                type="text"
                placeholder="🔍 Search Invoice / Customer / Phone..."
                value={salesSearch}
                onChange={e => setSalesSearch(e.target.value)}
                className="input-field"
                style={{ width: '280px', marginBottom: 0 }}
              />
            </div>

            {/* Main Sales Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                  <th style={{ padding: '15px' }}>DATE</th>
                  <th>INVOICE ID</th>
                  <th>CUSTOMER</th>
                  <th>TOTAL BILL</th>
                  <th>PAID</th>
                  <th>DUE</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {searchedSales.map(sale => (
                  <tr key={sale.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '15px', fontSize: '13px' }}>{formatDateTime(sale.createdAt)}</td>
                    <td><b style={{ color: '#4a00e0' }}>#{sale.invoiceId}</b></td>
                    <td>
                      {sale.name || "Walk-In"} 
                      <div style={{ fontSize: '11px', color: '#999' }}>{[sale.phone, sale.phone2].filter(Boolean).join(', ')}</div>
                    </td>
                    <td style={{ fontWeight: 'bold' }}>৳ {formatTk(sale.totalBill)}</td>
                    <td style={{ color: '#10b981', fontWeight: 'bold' }}>৳ {formatTk(sale.paid)}</td>
                    <td>
                      {Number(sale.dueAmount || 0) > 0 ? (
                        <span style={{ padding: '4px 10px', background: '#fce8e6', color: '#c5221f', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px' }}>
                          ৳ {formatTk(sale.dueAmount)}
                        </span>
                      ) : (
                        <span style={{ color: '#10b981', fontSize: '12px' }}>Paid</span>
                      )}
                    </td>
                    <td>
                      <button onClick={() => setSelectedInvoice(sale)} style={{ padding: '6px 15px', background: '#4a00e0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>View</button>
                    </td>
                  </tr>
                ))}
                {searchedSales.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No data found</td></tr>
                )}
              </tbody>
              {/* ---- নিচে মোট যোগফল ---- */}
              {searchedSales.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #f4f7fe', fontWeight: 'bold', background: '#f8f9ff' }}>
                    <td style={{ padding: '12px 15px' }} colSpan={3}>TOTAL ({searchedSales.length})</td>
                    <td>৳ {formatTk(salesTotals.bill)}</td>
                    <td style={{ color: '#10b981' }}>৳ {formatTk(salesTotals.paid)}</td>
                    <td style={{ color: '#c5221f' }}>৳ {formatTk(salesTotals.due)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>

            {/* Receivables Specific Section (Only Dues) */}
            {receivablesData.length > 0 && (
              <div style={{ marginTop: '40px' }}>
                <h4 style={{ marginBottom: '15px', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>⚠️</span> Detailed Receivables List
                </h4>
                <div style={{ borderRadius: '15px', overflow: 'hidden', border: '1px solid #ffeeba' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#fff9e6' }}>
                      <tr style={{ textAlign: 'left', color: '#856404' }}>
                        <th style={{ padding: '12px 15px' }}>Customer Name</th>
                        <th>Phone</th>
                        <th>Invoice ID</th>
                        <th>Date</th>
                        <th style={{ textAlign: 'right', paddingRight: '15px' }}>Due Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receivablesData.map(r => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #fff3cd', background: '#fff' }}>
                          <td style={{ padding: '12px 15px', fontWeight: 'bold' }}>{r.name || 'Walk-In'}</td>
                          <td>{r.phone || 'N/A'}</td>
                          <td>#{r.invoiceId}</td>
                          <td style={{ fontSize: '12px' }}>{formatDateTime(r.createdAt)}</td>
                          <td style={{ textAlign: 'right', paddingRight: '15px', color: '#c5221f', fontWeight: 'bold' }}>
                            ৳ {formatTk(r.dueAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* --- PURCHASE TAB CONTENT --- */}
        {activeTab === 'purchases' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
              <h4 style={{ margin: 0, color: '#4a00e0' }}>All Purchase Transactions</h4>
              <input
                type="text"
                placeholder="🔍 Search Invoice / Supplier..."
                value={purchaseSearch}
                onChange={e => setPurchaseSearch(e.target.value)}
                className="input-field"
                style={{ width: '280px', marginBottom: 0 }}
              />
            </div>

            {purchaseList.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                No data found in the "purchases" collection.
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                    <th style={{ padding: '15px' }}>DATE</th>
                    <th>INVOICE ID</th>
                    <th>SUPPLIER</th>
                    <th>TOTAL BILL</th>
                    <th>PAID</th>
                    <th>DUE</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {searchedPurchases.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                      <td style={{ padding: '15px', fontSize: '13px' }}>{formatDateTime(p.createdAt)}</td>
                      <td><b style={{ color: '#4a00e0' }}>#{getPurchaseInvoice(p)}</b></td>
                      <td>{getSupplierName(p)}</td>
                      <td style={{ fontWeight: 'bold' }}>৳ {formatTk(getPurchaseTotal(p))}</td>
                      <td style={{ color: '#10b981', fontWeight: 'bold' }}>৳ {formatTk(getPurchasePaid(p))}</td>
                      <td>
                        {getPurchaseDue(p) > 0 ? (
                          <span style={{ padding: '4px 10px', background: '#fce8e6', color: '#c5221f', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px' }}>
                            ৳ {formatTk(getPurchaseDue(p))}
                          </span>
                        ) : (
                          <span style={{ color: '#10b981', fontSize: '12px' }}>Paid</span>
                        )}
                      </td>
                      <td>
                        <button onClick={() => setSelectedPurchase(p)} style={{ padding: '6px 15px', background: '#4a00e0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>View</button>
                      </td>
                    </tr>
                  ))}
                  {searchedPurchases.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No data found</td></tr>
                  )}
                </tbody>
                {searchedPurchases.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #f4f7fe', fontWeight: 'bold', background: '#f8f9ff' }}>
                      <td style={{ padding: '12px 15px' }} colSpan={3}>TOTAL ({searchedPurchases.length})</td>
                      <td>৳ {formatTk(purchaseTotals.bill)}</td>
                      <td style={{ color: '#10b981' }}>৳ {formatTk(purchaseTotals.paid)}</td>
                      <td style={{ color: '#c5221f' }}>৳ {formatTk(purchaseTotals.due)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </>
        )}

        {/* --- RETURNS TAB CONTENT --- */}
        {activeTab === 'returns' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
              <h4 style={{ margin: 0, color: '#4a00e0' }}>All Return Transactions</h4>
              <input
                type="text"
                placeholder="🔍 Search Return ID / Invoice / Customer / Reason..."
                value={returnSearch}
                onChange={e => setReturnSearch(e.target.value)}
                className="input-field"
                style={{ width: '320px', marginBottom: 0 }}
              />
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                  <th style={{ padding: '15px' }}>DATE</th>
                  <th>RETURN ID</th>
                  <th>ORIGINAL INVOICE</th>
                  <th>CUSTOMER</th>
                  <th>REASON</th>
                  <th>REFUND</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {searchedReturns.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '15px', fontSize: '13px' }}>{formatDateTime(r.createdAt)}</td>
                    <td><b style={{ color: '#4a00e0' }}>#{r.returnId}</b></td>
                    <td>#{r.originalInvId}</td>
                    <td>{r.customerName}</td>
                    <td>{r.reason || 'N/A'}</td>
                    <td style={{ color: 'red', fontWeight: 'bold' }}>৳ {formatTk(r.totalRefund)}</td>
                    <td>
                      <button onClick={() => setSelectedReturn(r)} style={{ padding: '6px 15px', background: '#4a00e0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>View</button>
                    </td>
                  </tr>
                ))}
                {searchedReturns.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No data found</td></tr>
                )}
              </tbody>
              {searchedReturns.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #f4f7fe', fontWeight: 'bold', background: '#f8f9ff' }}>
                    <td style={{ padding: '12px 15px' }} colSpan={5}>TOTAL ({searchedReturns.length})</td>
                    <td style={{ color: '#c5221f' }}>৳ {formatTk(returnTotals.refund)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </>
        )}

        {/* --- PROFIT TAB CONTENT --- */}
        {activeTab === 'profit' && (
          <>
            <p style={{ fontSize: '12px', color: '#888', marginBottom: '20px', background: '#f8f9ff', padding: '10px 15px', borderRadius: '10px' }}>
              ℹ️ This is an <b>estimated (cash-flow based)</b> profit figure — calculated by subtracting
              total purchases and total return refunds from total sales in the selected period. It is not an
              exact gross-margin calculation based on each product's actual cost price, because the cost
              price isn't stored separately for every individual sale.
              <br />
              🚚 <b>Delivery charge is never counted as profit.</b> When the customer pays it, it's excluded
              from Sales Revenue (it was never real merchandise revenue). When the shop pays it, it's already
              subtracted directly from Net Profit below — do <b>not</b> also add it as a manual expense in
              Accounts, or it will be counted twice. Only use Accounts → Expense → Delivery Charge for
              delivery costs that aren't tied to any specific invoice.
              <br />
              📦 <b>Pathao's COD fee (1% of the COD amount)</b> is a separate cost on top of the delivery charge. It is
              subtracted from Net Profit automatically for every courier sale with COD — do <b>not</b> also add it as a
              manual expense in Accounts.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>
              <div className="hover-lift" style={{ background: '#eafff3', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #10b981' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#218c61', fontWeight: 'bold' }}>TOTAL SALES REVENUE</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#218c61' }}>৳ {formatTk(profitSummary.totalSalesRevenue)}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#218c61' }}>excluding delivery charge</p>
              </div>
              <div className="hover-lift" style={{ background: '#eef4ff', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #2e86de' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#1b5fa8', fontWeight: 'bold' }}>DELIVERY CHARGE</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#1b5fa8' }}>৳ {formatTk(profitSummary.deliveryCharge)}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#1b5fa8' }}>
                  🟢 customer ৳ {formatTk(deliveryTotals.customer)} · 🔴 shop ৳ {formatTk(deliveryTotals.shop)}
                </p>
              </div>
              <div className="hover-lift" style={{ background: '#fff8e1', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #d97706' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#8a6d3b', fontWeight: 'bold' }}>PATHAO COD FEE (1%)</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#8a6d3b' }}>৳ {formatFee(profitSummary.codFee)}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#8a6d3b' }}>your cost — subtracted from profit</p>
              </div>
              <div className="hover-lift" style={{ background: '#fff0f0', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #e74c3c' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#c0392b', fontWeight: 'bold' }}>TOTAL PURCHASE COST</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#c0392b' }}>৳ {formatTk(profitSummary.totalPurchaseCost)}</h3>
              </div>
              <div className="hover-lift" style={{ background: '#fff8e1', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #f59e0b' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#b7791f', fontWeight: 'bold' }}>TOTAL RETURN REFUND</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#b7791f' }}>৳ {formatTk(profitSummary.totalReturnRefund)}</h3>
              </div>
              <div className="hover-lift" style={{ background: '#f0f0ff', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #4a00e0' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#4a00e0', fontWeight: 'bold' }}>NET PROFIT (ESTIMATED)</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#4a00e0' }}>৳ {formatTk(profitSummary.netProfit)}</h3>
              </div>
            </div>
          </>
        )}

        {/* --- DUE RECEIVE TAB CONTENT --- */}
        {activeTab === 'receive' && (
          <>
            <div className="hover-lift" style={{
              background: '#fff', padding: '20px', borderRadius: '20px',
              borderLeft: '6px solid #10b981', boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
              marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '15px', width: 'fit-content', minWidth: '300px'
            }}>
              <div style={{ fontSize: '30px' }}>💰</div>
              <div>
                <p style={{ margin: 0, color: '#666', fontSize: '14px', fontWeight: 'bold' }}>TOTAL RECEIVED (this period)</p>
                <h2 style={{ margin: 0, color: '#10b981', fontSize: '24px' }}>৳ {formatTk(receiveTotals.amount)}</h2>
              </div>
            </div>

            <h4 style={{ margin: '0 0 15px 0', color: '#4a00e0' }}>All Due Receives</h4>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                  <th style={{ padding: '15px' }}>DATE</th>
                  <th>INVOICE ID</th>
                  <th>CUSTOMER</th>
                  <th>PHONE</th>
                  <th>AMOUNT RECEIVED</th>
                </tr>
              </thead>
              <tbody>
                {filteredReceives.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '15px', fontSize: '13px' }}>{formatDateTime(r.createdAt)}</td>
                    <td onClick={() => viewReceiveInvoice(r)} style={{ cursor: 'pointer' }}><b style={{ color: '#4a00e0' }}>#{r.invoiceId}</b></td>
                    <td>{r.customerName || 'Walk-In'}</td>
                    <td>{r.customerPhone}</td>
                    <td style={{ color: '#10b981', fontWeight: 'bold' }}>৳ {formatTk(r.amount)}</td>
                  </tr>
                ))}
                {filteredReceives.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No data found</td></tr>
                )}
              </tbody>
              {filteredReceives.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #f4f7fe', fontWeight: 'bold', background: '#f8f9ff' }}>
                    <td style={{ padding: '12px 15px' }} colSpan={4}>TOTAL ({filteredReceives.length})</td>
                    <td style={{ color: '#10b981' }}>৳ {formatTk(receiveTotals.amount)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </>
        )}

        {/* --- DELIVERY CHARGE TAB CONTENT --- */}
        {activeTab === 'delivery' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '15px', marginBottom: '25px' }}>
              <div className="hover-lift" style={{ background: '#eafff3', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #10b981' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#218c61', fontWeight: 'bold' }}>🟢 PAID BY CUSTOMER</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#218c61' }}>৳ {formatTk(deliveryTotals.customer)}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#218c61' }}>collected on the bill, goes to courier</p>
              </div>
              <div className="hover-lift" style={{ background: '#ffebee', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #e74c3c' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#c0392b', fontWeight: 'bold' }}>🔴 PAID BY SHOP</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#c0392b' }}>৳ {formatTk(deliveryTotals.shop)}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#c0392b' }}>your own cost, cuts into profit</p>
              </div>
              <div className="hover-lift" style={{ background: '#eef4ff', padding: '18px', borderRadius: '15px', borderLeft: '5px solid #2e86de' }}>
                <p style={{ margin: 0, fontSize: '12px', color: '#1b5fa8', fontWeight: 'bold' }}>🚚 TOTAL DELIVERY</p>
                <h3 style={{ margin: '5px 0 0 0', color: '#1b5fa8' }}>৳ {formatTk(deliveryTotals.all)}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#1b5fa8' }}>{deliveryData.length} invoice(s) in this period</p>
              </div>
            </div>

            <p style={{ fontSize: '12px', color: '#888', marginBottom: '20px', background: '#f8f9ff', padding: '10px 15px', borderRadius: '10px' }}>
              ℹ️ Delivery charge is <b>never counted as profit</b>. When the customer pays it, the money is
              simply passed on to the courier. When the shop pays it, it is already counted as a cost here —
              so do <b>not</b> add those invoices again as a manual expense in Accounts (use the Accounts
              expense entry only for delivery costs that are not tied to any invoice).
              <br />
              ↔️ Picked the wrong one while selling? The <b>Admin account</b> can use the Change button on
              that row — the invoice total and due amount are corrected automatically, and every change is
              logged (who changed it, when, from what to what) so it stays trackable.
            </p>

            <h4 style={{ margin: '0 0 15px 0', color: '#4a00e0' }}>Delivery Charge by Invoice</h4>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                  <th style={{ padding: '15px' }}>DATE</th>
                  <th>INVOICE ID</th>
                  <th>CUSTOMER</th>
                  <th>PAID BY</th>
                  <th>DELIVERY CHARGE</th>
                  <th>FIX</th>
                </tr>
              </thead>
              <tbody>
                {deliveryData.map(s => {
                  const shopPaid = s.deliveryPaidBy === 'shop';
                  return (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                      <td style={{ padding: '15px', fontSize: '13px' }}>{formatDateTime(s.createdAt)}</td>
                      <td onClick={() => setSelectedInvoice(s)} style={{ cursor: 'pointer' }}><b style={{ color: '#4a00e0' }}>#{s.invoiceId}</b></td>
                      <td>{s.name || 'Walk-In'}</td>
                      <td>
                        <span style={{
                          padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold',
                          background: shopPaid ? '#ffebee' : '#eafff3',
                          color: shopPaid ? '#c0392b' : '#218c61'
                        }}>
                          {shopPaid ? `🔴 ${s.deliveryPaidByName || 'Kizuna'}` : `🟢 ${s.name || 'Customer'}`}
                        </span>
                      </td>
                      <td style={{ color: shopPaid ? '#c0392b' : '#218c61', fontWeight: 'bold' }}>৳ {formatTk(s.deliveryCharge)}</td>
                      <td>
                        {isAdmin ? (
                          <button
                            onClick={() => changeDeliveryPayer(s)}
                            title={shopPaid ? 'Change to customer-paid' : 'Change to shop-paid'}
                            style={{ padding: '5px 12px', borderRadius: '8px', border: '1px solid #ddd', background: '#f8f9ff', color: '#4a00e0', cursor: 'pointer', fontWeight: 'bold', fontSize: '11px', whiteSpace: 'nowrap' }}
                          >
                            ↔ Change to {shopPaid ? 'Customer' : 'Shop'}
                          </button>
                        ) : (
                          <span title="Only the Admin account can change this" style={{ fontSize: '11px', color: '#bbb' }}>🔒 Admin only</span>
                        )}
                        {(s.deliveryPayerChangeLog || []).length > 0 && (
                          <div style={{ marginTop: '6px', fontSize: '10px', color: '#999' }}>
                            {s.deliveryPayerChangeLog.map((log, li) => (
                              <div key={li}>
                                ↺ {log.from} → {log.to} by {log.changedBy}, {formatDateTime(log.changedAt)}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {deliveryData.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No delivery charge found in this period</td></tr>
                )}
              </tbody>
              {deliveryData.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #f4f7fe', fontWeight: 'bold', background: '#f8f9ff' }}>
                    <td style={{ padding: '12px 15px' }} colSpan={4}>TOTAL ({deliveryData.length})</td>
                    <td style={{ color: '#2e86de' }}>৳ {formatTk(deliveryTotals.all)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </>
        )}

      </div>

      {/* Sales Invoice Modal — পুরো ব্র্যান্ডেড ইনভয়েস, Sales/Due পেজের মতোই */}
      {selectedInvoice && (
        <div onClick={() => setSelectedInvoice(null)} style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.6)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000, padding: '20px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', padding:'30px', borderRadius:'25px', width:'450px', maxHeight: '90vh', overflowY: 'auto', position:'relative', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <button onClick={()=>setSelectedInvoice(null)} style={{ position:'absolute', top:15, right:15, border:'none', background:'#ffeded', color:'red', width:'30px', height:'30px', borderRadius:'50%', cursor:'pointer', fontWeight:'bold' }}>×</button>

            <div style={{ textAlign:'center', marginBottom:'15px' }}>
              <h1 style={{ margin:0, color:'#4a00e0', fontSize:'22px' }}>PB KIZUNA COMPANY LIMITED</h1>
              <p style={{ margin:'4px 0', fontSize:'11px', fontWeight:'bold' }}>H-10, R-2, Section-2, Block-D, <br/>Aftabnagar, Dhaka-1212</p>
              <p style={{ margin:0, fontSize:'11px' }}>Mobile: 01884-711844</p>
              <div style={{ borderBottom:'1px dashed #000', margin:'12px 0' }}></div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', letterSpacing: '1px' }}>SALES INVOICE</h4>
            </div>

            <div style={{ display:'flex', gap:'10px', fontSize:'12px', marginBottom:'12px' }}>
              <div style={{ flex:1 }}>
                <p style={{ margin: '2px 0' }}><b>Sold By:</b> {selectedInvoice.soldBy}</p>
                <p style={{ margin: '2px 0' }}><b>Date:</b> {selectedInvoice.dateTime || formatDateTime(selectedInvoice.createdAt)}</p>
                <p style={{ margin: '2px 0' }}><b>Invoice ID:</b> #{selectedInvoice.invoiceId}</p>
              </div>
              <div style={{ flex:1, border:'1px solid #ddd', borderRadius:'8px', padding:'8px 10px', background:'#f8f9ff' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight:'bold', color:'#4a00e0' }}>Bill To</p>
                <p style={{ margin: '2px 0' }}>{selectedInvoice.name || 'Walk-in Customer'}</p>
                {[selectedInvoice.phone, selectedInvoice.phone2].filter(Boolean).length > 0 && (
                  <p style={{ margin: '2px 0' }}>{[selectedInvoice.phone, selectedInvoice.phone2].filter(Boolean).join(', ')}</p>
                )}
                {selectedInvoice.address && <p style={{ margin: '2px 0' }}>{selectedInvoice.address}</p>}
              </div>
            </div>
            <div style={{ borderBottom:'1px dashed #000', margin:'8px 0 12px' }}></div>

            <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #eee' }}>
                  <th align='left' style={{ paddingBottom: '4px' }}>ITEM</th>
                  <th align='center' style={{ paddingBottom: '4px' }}>QTY</th>
                  <th align='right' style={{ paddingBottom: '4px' }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {(selectedInvoice.items || []).map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{padding:'5px 0'}}>
                      <b>{it.name}</b>
                      {it.itemDiscAmount > 0 && (
                        <div style={{fontSize:'10px', color:'#e74c3c'}}>Disc: -৳ {formatTk(it.itemDiscAmount)}</div>
                      )}
                    </td>
                    <td align='center'>{it.qty}</td>
                    <td align='right'>{formatTk(it.finalItemTotal ?? (it.price * it.qty))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ borderTop:'1px solid #eee', marginTop:'10px', paddingTop:'8px', textAlign:'right', fontSize:'12px' }}>
              {selectedInvoice.subtotal > 0 && <p style={{ margin: '3px 0' }}>Sub Total: ৳ {formatTk(selectedInvoice.subtotal)}</p>}
              {selectedInvoice.totalDiscount > 0 && (
                <p style={{ margin: '3px 0', color: '#e74c3c' }}>Total Discount: -৳ {formatTk(selectedInvoice.totalDiscount)}</p>
              )}
              {selectedInvoice.deliveryCharge > 0 && (
                selectedInvoice.deliveryPaidBy === 'shop' ? (
                  <p style={{ margin: '3px 0', color: '#c0392b' }}>Delivery Charge: ৳ {formatTk(selectedInvoice.deliveryCharge)} (paid by {selectedInvoice.deliveryPaidByName})</p>
                ) : (
                  <p style={{ margin: '3px 0' }}>Delivery Charge: +৳ {formatTk(selectedInvoice.deliveryCharge)}</p>
                )
              )}
              <h3 style={{ margin:'6px 0', fontSize: '14px' }}>Grand Total: ৳ {formatTk(selectedInvoice.totalBill)}</h3>
              <p style={{ margin: '3px 0' }}>Paid: ৳ {formatTk(selectedInvoice.paid)}</p>
              {Number(selectedInvoice.dueAmount || 0) > 0 ? (
                <p style={{ margin: '3px 0', color: '#e74c3c', fontWeight: 'bold' }}>Due: ৳ {formatTk(selectedInvoice.dueAmount)}</p>
              ) : (
                <p style={{ margin: '3px 0', color: '#10b981', fontWeight: 'bold' }}>Fully Paid</p>
              )}
            </div>

            <button
              onClick={() => printSalesInvoice(selectedInvoice)}
              className="btn-confirm"
              style={{ width: '100%', marginTop: '15px', padding: '10px', fontSize: '13px' }}
            >
              Print Invoice 🖨️
            </button>
          </div>
        </div>
      )}

      {/* Purchase Voucher Modal — পুরো ব্র্যান্ডেড ভাউচার, Purchase পেজের প্রিন্ট ফরম্যাটের মতোই */}
      {selectedPurchase && (
        <div onClick={() => setSelectedPurchase(null)} style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.6)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000, padding: '20px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', padding:'30px', borderRadius:'25px', width:'450px', maxHeight: '90vh', overflowY: 'auto', position:'relative', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <button onClick={()=>setSelectedPurchase(null)} style={{ position:'absolute', top:15, right:15, border:'none', background:'#ffeded', color:'red', width:'30px', height:'30px', borderRadius:'50%', cursor:'pointer', fontWeight:'bold' }}>×</button>

            <div style={{ textAlign:'center', marginBottom:'15px' }}>
              <h1 style={{ margin:0, color:'#4a00e0', fontSize:'22px' }}>PB KIZUNA COMPANY LIMITED</h1>
              <p style={{ margin:'4px 0', fontSize:'11px', fontWeight:'bold' }}>H-10, R-2, Section-2, Block-D, <br/>Aftabnagar, Dhaka-1212</p>
              <p style={{ margin:0, fontSize:'11px' }}>Mobile: 01884-711844</p>
              <div style={{ borderBottom:'1px dashed #000', margin:'12px 0' }}></div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', letterSpacing: '1px' }}>PURCHASE VOUCHER / INVOICE</h4>
            </div>

            <div style={{ fontSize:'12px', marginBottom:'12px' }}>
              <p style={{ margin: '2px 0' }}><b>Added By:</b> {selectedPurchase.addedBy || 'N/A'}</p>
              <p style={{ margin: '2px 0' }}><b>Business Type:</b> {getBusinessType(selectedPurchase)}</p>
              <p style={{ margin: '2px 0' }}><b>Date:</b> {formatDateTime(selectedPurchase.createdAt || selectedPurchase.date)}</p>
              <p style={{ margin: '2px 0' }}><b>Purchase ID:</b> #{getPurchaseInvoice(selectedPurchase)}</p>
              <p style={{ margin: '2px 0' }}><b>Supplier:</b> {getSupplierName(selectedPurchase)} {selectedPurchase.supplierPhone && selectedPurchase.supplierPhone !== 'N/A' ? `(${selectedPurchase.supplierPhone})` : ''}</p>
              <div style={{ borderBottom:'1px dashed #000', margin:'8px 0' }}></div>
            </div>

            <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #eee' }}>
                  <th align='left' style={{ paddingBottom: '4px' }}>ITEM</th>
                  <th align='center' style={{ paddingBottom: '4px' }}>QTY</th>
                  <th align='right' style={{ paddingBottom: '4px' }}>UNIT COST</th>
                  <th align='right' style={{ paddingBottom: '4px' }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f9f9f9' }}>
                  <td style={{padding:'5px 0'}}><b>{selectedPurchase.productName || getSupplierName(selectedPurchase)}</b></td>
                  <td align='center'>{selectedPurchase.quantity}</td>
                  <td align='right'>{formatTk(selectedPurchase.costPrice)}</td>
                  <td align='right'>{formatTk(getPurchaseTotal(selectedPurchase))}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ borderTop:'1px solid #eee', marginTop:'10px', paddingTop:'8px', textAlign:'right', fontSize:'12px' }}>
              {selectedPurchase.baseRate > 0 && (
                <p style={{ margin: '3px 0' }}>Base Rate (x{selectedPurchase.quantity}): ৳ {formatTk(selectedPurchase.baseRate * selectedPurchase.quantity)}</p>
              )}
              {selectedPurchase.otherCost > 0 && (
                <p style={{ margin: '3px 0' }}>Transport / Other Cost: +৳ {formatTk(selectedPurchase.otherCost)}</p>
              )}
              <h3 style={{ margin:'6px 0', fontSize: '14px' }}>Total Cost: ৳ {formatTk(getPurchaseTotal(selectedPurchase))}</h3>
            </div>

            <button
              onClick={() => printPurchaseVoucher(selectedPurchase)}
              className="btn-confirm"
              style={{ width: '100%', marginTop: '15px', padding: '10px', fontSize: '13px' }}
            >
              Print Voucher 🖨️
            </button>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {selectedReturn && (
        <div style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', background:'rgba(0,0,0,0.6)', display:'flex', justifyContent:'center', alignItems:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', padding:'30px', borderRadius:'20px', width:'400px', maxHeight: '90vh', overflowY: 'auto', position:'relative' }}>
            <button onClick={()=>setSelectedReturn(null)} style={{ position:'absolute', top:15, right:15, border:'none', background:'none', fontSize:'20px', cursor:'pointer' }}>×</button>
            <h3 style={{ textAlign:'center', color:'#4a00e0' }}>Return Details</h3>
            <hr/>
            <p><b>Return ID:</b> #{selectedReturn.returnId}</p>
            <p><b>Original Invoice:</b> #{selectedReturn.originalInvId}</p>
            <p><b>Customer:</b> {selectedReturn.customerName}</p>
            <p><b>Reason:</b> {selectedReturn.reason || 'N/A'}</p>
            <div style={{ margin: '15px 0', borderTop: '1px dashed #ccc', paddingTop: '10px' }}>
              {selectedReturn.items?.map((it, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', marginBottom:'5px' }}>
                  <span>{it.name} x {it.returnQty}</span>
                  <span>৳ {formatTk(it.returnTotal)}</span>
                </div>
              ))}
            </div>
            <div style={{ textAlign:'right' }}>
              {selectedReturn.deliveryChargeDeduction > 0 && (
                <p style={{ margin: '3px 0', fontSize:'13px', color: '#666' }}>Delivery/Other Deduction: -৳ {formatTk(selectedReturn.deliveryChargeDeduction)}</p>
              )}
              <p style={{ fontWeight:'bold', color: 'red' }}>Total Refund: ৳ {formatTk(selectedReturn.totalRefund)}</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Reports;